import React from 'react'
import { Typography } from '@mui/material'
import {
  Page,
  Section,
  Bullets,
  Bullet,
  Info,
  Warning,
  Step,
  Screenshot,
} from '../../components/DocsComponents.jsx'
import DocLink from '../../components/DocLink.jsx'

const PaymentsUnpaid = () => (
  <Page
    title="Payments and Unpaid Invoices"
    intro="Record customer payments against any invoice — including part-payments — and
    use the Unpaid Invoices page to chase event invoices that are still open after the
    event has finished."
  >
    <Section title="Recording a payment">
      <Step number={1} title="Open the invoice">
        <Typography>
          From <DocLink url="/invoices" text="Invoices" />, click the invoice
          the customer has paid, then choose <strong>Record Payment</strong>.
        </Typography>
      </Step>
      <Step number={2} title="Check the amount">
        <Typography>
          The amount is pre-filled with the full outstanding balance. For a
          part-payment, change it to the amount actually received — the rest
          stays outstanding and further payments can be recorded later.
        </Typography>
      </Step>
      <Step number={3} title="Set the date paid and the payment method">
        <Typography>
          The date defaults to today and cannot be in the future. The{' '}
          <strong>payment method</strong> is required — Bank Transfer, Card,
          Cash, or Other. Use the note for anything else that will help
          reconciliation, like the remittance reference or who took it.
        </Typography>
      </Step>
      <Step number={4} title="Submit">
        <Typography>
          The payment appears in the invoice's Payments table, the Paid /
          Outstanding figures update immediately, and the invoice's status
          moves to Partially Paid or Paid automatically.
        </Typography>
      </Step>
      <Info>
        Payments can only be recorded against invoices that have been approved
        — the server blocks payments on Draft invoices — and a payment cannot
        exceed the invoice's remaining balance. For a customer settling in
        full, <strong>Mark Paid</strong> on the invoice is quicker: it records
        the whole remaining balance as one payment and settles the invoice in
        a single action.
      </Info>
      <Warning>
        Payments cannot be edited or deleted from the app once submitted —
        check the amount, method, and date before saving. If a payment is
        entered wrongly, speak to your system administrator.
      </Warning>
      <Screenshot url="finance/record-payment.png">
        Record Payment dialog with amount, date paid, payment method, and note
        fields.
      </Screenshot>
    </Section>

    <Section title="Chasing unpaid invoices">
      <Typography>
        <DocLink url="/unpaid-invoices" text="Unpaid Invoices" /> (also reached
        from the Finance Centre's Overdue card) lists event invoices that are
        still open — anything not Draft and not Paid — for events that have
        actually finished and were not cancelled. A running{' '}
        <strong>Total Due</strong> shows the amount to chase.
      </Typography>
      <Bullets>
        <Bullet>
          Invoices on future or live events are deliberately excluded — an open
          invoice for an event that has not happened yet is not overdue.
        </Bullet>
        <Bullet>
          Click a row to open the event record, where the invoice and client
          contact details live.
        </Bullet>
      </Bullets>
      <Info>
        This page covers <strong>event</strong> invoices only. Unpaid PTS
        invoices are chased from the PTS invoicing screen, which has its own
        status workflow — see{' '}
        <DocLink
          url="/patient-transport/invoicing"
          text="PTS Quoting, Invoicing & Billing"
          docsPage
        />
        . Overall overdue figures across both sources are on the{' '}
        <DocLink url="/finance/overview" text="Finance Centre" docsPage /> and
        in the{' '}
        <DocLink url="/finance/finance-kpis" text="Finance KPIs" docsPage />.
      </Info>
    </Section>

    <Section title="Who can do this?">
      <Bullets>
        <Bullet>
          <strong>Recording a payment or using Mark Paid</strong> — Finance
          team.
        </Bullet>
        <Bullet>
          <strong>Unpaid Invoices page</strong> — Event Planning team (this is
          the role the server checks for that list).
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default PaymentsUnpaid
